import { Button } from "@/components/ui/button";
import LanguageSwitcher, { Language } from "./LanguageSwitcher";
import { Play, ChevronDown, User, LogOut, Menu, X } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

interface HeaderProps {
  language: Language;
  onLanguageChange: (language: Language) => void;
  content: {
    navigation: {
      services: string;
      about: string;
      process: string;
      pricing: string;
      contact: string;
    };
    cta: string;
    ctaUrl: string;
  };
}

const Header = ({ language, onLanguageChange, content }: HeaderProps) => {
  const { user, signOut } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white backdrop-blur-md border-b border-border">
      <div className="container mx-auto px-4 py-2 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img 
            src="/volym-studio-logo.svg" 
            alt="Volym Studio Logo" 
            className="w-12 h-12 bg-transparent"
            style={{ backgroundColor: 'transparent' }}
          />
        </Link>
        
        <nav className="hidden md:flex items-center gap-8">
          <NavigationMenu>
            <NavigationMenuList>
              <NavigationMenuItem>
                <NavigationMenuTrigger className="text-sm font-medium">
                  {content.navigation.services}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <div className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px]">
                    <NavigationMenuLink asChild>
                      <Link
                        to="/reklamfilm"
                        className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                      >
                        <div className="text-sm font-medium leading-none">Reklamfilm</div>
                        <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                          Engagerande annonser för sociala medier
                        </p>
                      </Link>
                    </NavigationMenuLink>
                    <NavigationMenuLink asChild>
                      <Link
                        to="/videoavatarer"
                        className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                      >
                        <div className="text-sm font-medium leading-none">Videoavatar</div>
                        <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                          AI-avatarer som levererar ditt budskap
                        </p>
                      </Link>
                    </NavigationMenuLink>
                    <NavigationMenuLink asChild>
                      <Link
                        to="/explainer-video"
                        className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                      >
                        <div className="text-sm font-medium leading-none">Explainer-video</div>
                        <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                          Animerade förklaringsvideofilmer
                        </p>
                      </Link>
                    </NavigationMenuLink>
                    <NavigationMenuLink asChild>
                      <Link
                        to="/animerad-film"
                        className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                      >
                        <div className="text-sm font-medium leading-none">Animerad film</div>
                        <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
                          Fullständigt animerade filmer
                        </p>
                      </Link>
                    </NavigationMenuLink>
                  </div>
                </NavigationMenuContent>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>
          <Link to="/om-oss" className="text-sm font-medium hover:text-primary transition-colors">
            {content.navigation.about}
          </Link>
          <Link to="/pricing" className="text-sm font-medium hover:text-primary transition-colors">
            {content.navigation.pricing}
          </Link>
          <a href="#contact" className="text-sm font-medium hover:text-primary transition-colors">
            {content.navigation.contact}
          </a>
        </nav>

        <div className="flex items-center gap-4">
          <LanguageSwitcher 
            currentLanguage={language} 
            onLanguageChange={onLanguageChange} 
          />
          
          {/* Mobile Menu Button */}
          <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="sm" className="md:hidden">
                <Menu className="h-4 w-4" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[400px]">
              <SheetHeader>
                <SheetTitle>Navigation</SheetTitle>
              </SheetHeader>
              <nav className="flex flex-col gap-4 mt-8">
                <div className="space-y-4">
                  <div className="text-sm font-medium text-muted-foreground">Tjänster</div>
                  <div className="pl-4 space-y-3">
                    <Link
                      to="/reklamfilm"
                      className="block text-sm hover:text-primary transition-colors"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Reklamfilm
                    </Link>
                    <Link
                      to="/videoavatarer"
                      className="block text-sm hover:text-primary transition-colors"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Videoavatar
                    </Link>
                    <Link
                      to="/explainer-video"
                      className="block text-sm hover:text-primary transition-colors"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Explainer-video
                    </Link>
                    <Link
                      to="/animerad-film"
                      className="block text-sm hover:text-primary transition-colors"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Animerad film
                    </Link>
                  </div>
                </div>
                <Link
                  to="/om-oss"
                  className="text-sm font-medium hover:text-primary transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {content.navigation.about}
                </Link>
                <Link
                  to="/pricing"
                  className="text-sm font-medium hover:text-primary transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {content.navigation.pricing}
                </Link>
                <a
                  href="#contact"
                  className="text-sm font-medium hover:text-primary transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {content.navigation.contact}
                </a>
              </nav>
            </SheetContent>
          </Sheet>
          
          <div className="hidden md:flex">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    {user.email}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Profil
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut} className="flex items-center gap-2">
                    <LogOut className="h-4 w-4" />
                    Logga ut
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button asChild variant="ghost" size="sm">
                <Link to="/auth">Logga in</Link>
              </Button>
            )}
          </div>
          
          <Button asChild variant="default" size="sm" className="bg-gradient-primary border-0 hover:opacity-90">
            <a href={content.ctaUrl} target="_self">
              {content.cta}
            </a>
          </Button>
        </div>
      </div>
    </header>
  );
};

export default Header;